import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploadField from '../../components/ImageUploadField';
import {
  getTtfPieces,
  createTtfPiece,
  updateTtfPiece,
  deleteTtfPiece,
  getTtfPieceRequests,
  updateTtfPieceRequest,
} from '../../services/adminService';
import { computeTtfPriceMxn, toWhatsAppNumber } from '../../utils/ttf';
import { formatPrice } from '../../utils/format';

const TTF_PIECES_BUCKET = 'ttf_pieces';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// Catalog availability shown on the customer cards.
const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Disponible', color: '#555' },
  { value: 'obtained', label: 'Conseguido', color: '#28a745' },
  { value: 'sold_out', label: 'Agotada', color: '#dc3545' },
];
const availabilityOf = (piece) => (piece.is_sold_out ? 'sold_out' : piece.is_obtained ? 'obtained' : 'available');

const REQUEST_STATUS_LABELS = { new: 'Nueva', added: 'Agregada', dismissed: 'Descartada' };

const helperStyle = { fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' };

const emptyForm = {
  image_url: '',
  name: '',
  name_en: '',
  artist_name: '',
  description: '',
  description_en: '',
  original_price: '',
  is_lottery: false,
};

const toForm = (piece) => ({
  image_url: piece.image_url || '',
  name: piece.name || '',
  name_en: piece.name_en || '',
  artist_name: piece.artist_name || '',
  description: piece.description || '',
  description_en: piece.description_en || '',
  original_price: piece.original_price == null ? '' : String(piece.original_price),
  is_lottery: Boolean(piece.is_lottery),
});

const AdminTtfPiecesPage = () => {
  const { session } = useAuth();
  const token = session?.access_token;

  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [busyId, setBusyId] = useState(null);

  // Customer requests for pieces not in the catalog.
  const [requests, setRequests] = useState([]);
  const [requestsError, setRequestsError] = useState(null);
  const [fromRequestId, setFromRequestId] = useState(null);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); setError('No hay sesión activa.'); return; }
    setLoading(true);
    try {
      const data = await getTtfPieces(token);
      const list = data.pieces || [];
      setPieces(list);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token) return;
    const loadRequests = async () => {
      try {
        const data = await getTtfPieceRequests(token);
        setRequests(data.requests || []);
      } catch (err) {
        setRequestsError(err.message);
      }
    };
    loadRequests();
  }, [token]);

  const setRequestStatus = async (request, status) => {
    try {
      const { request: updated } = await updateTtfPieceRequest(token, request.id, { status });
      setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      alert(`Error al actualizar la solicitud: ${err.message}`);
    }
  };

  const handleUseRequest = (request) => {
    setEditingId(null);
    setFromRequestId(request.id);
    setForm({
      ...emptyForm,
      artist_name: request.artist_name || '',
      description: request.notes || '',
    });
    setFormError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const previewPrice = computeTtfPriceMxn(form.original_price);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFromRequestId(null);
    setFormError(null);
  };

  const handleEdit = (piece) => {
    setEditingId(piece.id);
    setForm(toForm(piece));
    setFormError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!form.name.trim()) { setFormError('El nombre de la pieza es requerido.'); return; }

    const body = {
      image_url: form.image_url.trim() || null,
      name: form.name.trim(),
      name_en: form.name_en.trim() || null,
      artist_name: form.artist_name.trim() || null,
      description: form.description.trim() || null,
      description_en: form.description_en.trim() || null,
      // Optional: without a price the piece is saved as a hidden draft.
      original_price: form.original_price === '' ? null : Number(form.original_price),
      is_lottery: form.is_lottery,
    };

    setSaving(true);
    try {
      if (editingId) {
        const { piece } = await updateTtfPiece(token, editingId, body);
        setPieces((prev) => prev.map((p) => (p.id === piece.id ? piece : p)));
      } else {
        const { piece } = await createTtfPiece(token, body);
        setPieces((prev) => [...prev, piece]);
        const request = requests.find((r) => r.id === fromRequestId);
        if (request) await setRequestStatus(request, 'added');
      }
      setForm(emptyForm);
      setEditingId(null);
      setFromRequestId(null);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const patchPiece = async (piece, changes) => {
    setBusyId(piece.id);
    try {
      const { piece: updated } = await updateTtfPiece(token, piece.id, changes);
      setPieces((prev) => prev.map((p) => (p.id === piece.id ? updated : p)));
    } catch (err) {
      alert(`Error al actualizar: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (piece) => {
    if (!window.confirm(`¿Eliminar "${piece.name}" del catálogo de Taipei Toy Festival?`)) return;
    setBusyId(piece.id);
    try {
      await deleteTtfPiece(token, piece.id);
      setPieces((prev) => prev.filter((p) => p.id !== piece.id));
      if (editingId === piece.id) resetForm();
    } catch (err) {
      alert(`Error al eliminar: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= pieces.length) return;

    const a = pieces[index];
    const b = pieces[target];
    setBusyId(a.id);
    try {
      await Promise.all([
        updateTtfPiece(token, a.id, { display_order: b.display_order }),
        updateTtfPiece(token, b.id, { display_order: a.display_order }),
      ]);
      const next = [...pieces];
      next[index] = { ...b, display_order: a.display_order };
      next[target] = { ...a, display_order: b.display_order };
      setPieces(next);
    } catch (err) {
      alert(`Error al reordenar: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const toggleButtonStyle = (busy, on, onColor) => ({
    background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer',
    color: busy ? '#ccc' : on ? onColor : '#aaa',
    fontWeight: '600', fontSize: '0.82rem', padding: 0, whiteSpace: 'nowrap',
  });

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Pedidos Taipei Toy Festival
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {pieces.length} pieza{pieces.length !== 1 ? 's' : ''}
        </span>
      </div>
      <p style={{ color: '#888', fontSize: '0.88rem', marginTop: 0, marginBottom: '1.5rem' }}>
        Página pública: <Link to="/pedidos/taipei-toy-festival">/pedidos/taipei-toy-festival</Link>. Los pedidos
        aparecen en <Link to="/admin/orders">Pedidos</Link> con la etiqueta "Taipei Toy Festival".
        {' '}<Link to="/admin/ttf-metrics">Ver métricas →</Link>
        <br />
        Catálogo de piezas disponibles para pedido. El precio final se calcula a partir del precio original
        (TWD) con un tipo de cambio fijo más 45% (incluye el retiro de efectivo en Taiwán), redondeado a los siguientes $100 MXN. El cliente solo ve el
        precio final. Una pieza sin precio se publica como "Precio por confirmar" y no se puede pedir hasta tener precio.
      </p>

      <form
        onSubmit={handleSubmit}
        style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1.25rem', marginBottom: '1.5rem' }}
      >
        <h2 style={{ fontSize: '1rem', color: '#333', marginTop: 0, marginBottom: '1rem' }}>
          {editingId ? 'Editar pieza' : fromRequestId ? 'Agregar pieza desde solicitud' : 'Agregar pieza'}
        </h2>

        <ImageUploadField
          label="Foto de la pieza"
          value={form.image_url}
          onChange={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
          bucket={TTF_PIECES_BUCKET}
          onUploadingChange={setIsUploading}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={labelStyle} htmlFor="ttf-name">Nombre *</label>
            <input id="ttf-name" type="text" value={form.name} onChange={setField('name')} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="ttf-name-en">Nombre (EN)</label>
            <input id="ttf-name-en" type="text" value={form.name_en} onChange={setField('name_en')} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="ttf-artist">Artista / marca</label>
            <input id="ttf-artist" type="text" value={form.artist_name} onChange={setField('artist_name')} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <label style={labelStyle} htmlFor="ttf-description">Descripción</label>
            <textarea id="ttf-description" rows={3} value={form.description} onChange={setField('description')} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="ttf-description-en">Descripción (EN)</label>
            <textarea id="ttf-description-en" rows={3} value={form.description_en} onChange={setField('description_en')} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1rem', alignItems: 'start' }}>
          <div>
            <label style={labelStyle} htmlFor="ttf-original-price">Precio original (TWD)</label>
            <input
              id="ttf-original-price" type="number" min="0" step="0.01"
              value={form.original_price} onChange={setField('original_price')} style={inputStyle}
            />
            <span style={helperStyle}>Opcional. Solo visible para admin. Sin precio, el cliente ve "Precio por confirmar".</span>
          </div>
          <div>
            <span style={labelStyle}>Precio final al cliente</span>
            <div data-testid="ttf-price-preview" style={{ fontSize: '1.4rem', fontWeight: '700', color: previewPrice ? '#333' : '#ccc', paddingTop: '0.2rem' }}>
              {previewPrice ? formatPrice(previewPrice, 'MXN') : 'Sin precio'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#444' }}>
            <input type="checkbox" checked={form.is_lottery} onChange={setField('is_lottery')} />
            Pieza por lotería
          </label>
        </div>

        {formError && <p style={{ color: '#dc3545', fontSize: '0.85rem', marginTop: '0.75rem' }}>{formError}</p>}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button
            type="submit"
            disabled={saving || isUploading}
            style={{
              padding: '0.5rem 1.25rem',
              background: saving || isUploading ? '#f8f9fa' : '#7c3aed',
              color: saving || isUploading ? '#aaa' : 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: saving || isUploading ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Guardando...' : isUploading ? 'Subiendo foto...' : editingId ? 'Guardar cambios' : '+ Agregar pieza'}
          </button>
          {(editingId || fromRequestId) && (
            <button
              type="button"
              onClick={resetForm}
              style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '0.9rem', color: '#666', cursor: 'pointer' }}
            >
              Cancelar
            </button>
          )}
        </div>
      </form>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Orden', 'Foto', 'Pieza', 'Original (TWD)', 'Precio final', 'Tipo', 'Estado', 'Visible', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td></tr>
              ) : pieces.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Aún no hay piezas en el catálogo.</td></tr>
              ) : pieces.map((piece, index) => {
                const busy = busyId === piece.id;
                return (
                  <tr key={piece.id} style={{ borderBottom: '1px solid #f8f8f8', background: editingId === piece.id ? '#faf5ff' : undefined }}>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                        <button
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0 || busy}
                          style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#ddd' : '#666', padding: 0, lineHeight: 1 }}
                          aria-label="Subir"
                        >▲</button>
                        <button
                          onClick={() => handleMove(index, 1)}
                          disabled={index === pieces.length - 1 || busy}
                          style={{ background: 'none', border: 'none', cursor: index === pieces.length - 1 ? 'default' : 'pointer', color: index === pieces.length - 1 ? '#ddd' : '#666', padding: 0, lineHeight: 1 }}
                          aria-label="Bajar"
                        >▼</button>
                      </div>
                    </td>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      {piece.image_url ? (
                        <img
                          src={piece.image_url}
                          alt={piece.name}
                          style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontWeight: '500', color: '#333' }}>{piece.name}</div>
                      {piece.artist_name && <div style={{ color: '#888', fontSize: '0.82rem' }}>{piece.artist_name}</div>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#888', whiteSpace: 'nowrap' }}>
                      {piece.original_price == null ? '—' : `${Number(piece.original_price).toLocaleString('es-MX')} TWD`}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#333', whiteSpace: 'nowrap' }}>
                      {piece.price_mxn == null
                        ? <span style={{ color: '#f59e0b', fontWeight: '600' }}>Sin precio</span>
                        : formatPrice(Number(piece.price_mxn), 'MXN')}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' }}>
                      {piece.is_lottery ? 'Lotería' : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <select
                        value={availabilityOf(piece)}
                        onChange={(e) => patchPiece(piece, { availability: e.target.value })}
                        disabled={busy}
                        aria-label={`Estado de ${piece.name}`}
                        style={{
                          padding: '0.3rem 0.5rem', border: '1px solid #ddd', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
                          background: 'white', color: AVAILABILITY_OPTIONS.find((o) => o.value === availabilityOf(piece)).color,
                        }}
                      >
                        {AVAILABILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <button onClick={() => patchPiece(piece, { active: !piece.active })} disabled={busy} style={toggleButtonStyle(busy, piece.active, '#28a745')}>
                        {piece.active ? 'Visible' : 'Oculta'}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleEdit(piece)}
                        disabled={busy}
                        style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', padding: 0, marginRight: '0.9rem' }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(piece)}
                        disabled={busy}
                        style={{ background: 'none', border: 'none', color: busy ? '#ccc' : '#dc3545', cursor: busy ? 'default' : 'pointer', fontSize: '0.85rem', fontWeight: '500', padding: 0 }}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <h2 style={{ fontSize: '1.1rem', color: '#333', margin: '2.5rem 0 0.5rem' }}>
        Solicitudes de clientes ({requests.filter((r) => r.status === 'new').length} nuevas)
      </h2>
      <p style={{ color: '#888', fontSize: '0.88rem', marginTop: 0, marginBottom: '1rem' }}>
        Piezas que los clientes pidieron y que no están en el catálogo. "Usar en formulario" llena el formulario de
        arriba; al guardar la pieza, la solicitud se marca como agregada.
      </p>
      {requestsError && <p style={{ color: '#dc3545' }}>{requestsError}</p>}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Fecha', 'Pieza', 'Cliente', 'Notas', 'Estado', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Sin solicitudes.</td></tr>
              ) : requests.map((request) => (
                <tr key={request.id} style={{ borderBottom: '1px solid #f8f8f8', opacity: request.status === 'dismissed' ? 0.55 : 1 }}>
                  <td style={{ padding: '0.75rem 1rem', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(request.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontWeight: '500', color: '#333' }}>{request.artist_name || 'Artista sin indicar'}</div>
                    {request.reference_url && (
                      <a href={request.reference_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', display: 'block' }}>Publicación</a>
                    )}
                    {request.wants_signature && (
                      <span style={{ background: '#f3e8ff', color: '#7c3aed', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600 }}>Quiere firma</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
                    <div>{request.customer_name}</div>
                    <a href={`https://instagram.com/${request.instagram}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>@{request.instagram}</a>
                    <a href={`https://wa.me/${toWhatsAppNumber(request.whatsapp)}`} target="_blank" rel="noopener noreferrer">WhatsApp {request.whatsapp}</a>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#666', fontSize: '0.85rem', maxWidth: 260 }}>{request.notes || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <select
                      value={request.status}
                      onChange={(e) => setRequestStatus(request, e.target.value)}
                      aria-label={`Estado de la solicitud ${request.id}`}
                      style={{ padding: '0.35rem 0.5rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '0.85rem', background: 'white' }}
                    >
                      {Object.entries(REQUEST_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    {request.status === 'new' && (
                      <button
                        onClick={() => handleUseRequest(request)}
                        style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', padding: 0 }}
                      >
                        Usar en formulario
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTtfPiecesPage;
